/**
 * Permission Management API Schemas
 *
 * Re-exports and extends existing schemas from permission.types.ts
 */

import { z } from 'zod';
import { Status } from '@pillar-portal/database/dist/enums';
import { ALL_AUTHORIZABLE_ACTIONS } from '../authorization-contracts';
import {
    AllowedWebsiteTemplateResourceDesignatorSchema,
    type AssignBundleRequest,
    CredentialResourceDesignatorSchema,
    type GrantPermissionRequest,
    IpgResourceDesignatorSchema,
    IpxCredentialDefinitionResourceDesignatorSchema,
    IpxWebResourceModelResourceDesignatorSchema,
    IpwResourceDesignatorSchema,
    type RepairAuditedPermissionRequest,
    type ListPublicBundlesResponse,
    type SharedPermissionBundlesSearchPageResponse,
    type SharedPermissionBundlesSearchRequest,
    type PolicyPermissionBundlesSearchRequest,
    type PolicyPermissionBundlesSearchPageResponse,
    type GrantPolicyPermissionBundleRequest,
    type ListPolicyPermissionGrantsResponse,
    type ReorderPolicyPermissionGrantsRequest,
    LogicalTerminalResourceDesignatorSchema,
    MerchantAccountResourceDesignatorSchema,
    OrganizationIpxCredentialPolicyResourceDesignatorSchema,
    OrganizationResourceDesignatorSchema,
    PaymentMethodTokenResourceDesignatorSchema,
    PaymentRequestResourceDesignatorSchema,
    PaymentTransactionResourceDesignatorSchema,
    PermissionPolicyResourceDesignatorSchema,
    PermissionResourceDisplayMetadataSchema,
    PermissionGrantSchema,
    RawPermissionGrantSchema,
    StoredPermissionGrantArrayParser,
    StoredPermissionGrantParser,
    PermissionSourceRoleAssignmentListItemSchema,
    PolicyPermissionBundleListItemSchema,
    type PermissionSourceRoleAssignmentsSearchPageResponse,
    type PermissionSourceRoleAssignmentsSearchRequest,
    PolicyPermissionBundleRoleAssignmentListItemSchema,
    type PolicyPermissionBundleRoleAssignmentsSearchPageResponse,
    type PolicyPermissionBundleRoleAssignmentsSearchRequest,
    PermissionSourceDesignatorSchema,
    PermissionSourceDesignatorType,
    PermissionsBundleResourceDesignatorSchema,
    PermissionsBundlePublicSchema,
    PermissionsBundleSchema,
    ProcessorResourceDesignatorSchema,
    ResourceDesignatorSchema,
    RoleAssignmentResourceDesignatorSchema,
    RpaModuleResourceDesignatorSchema,
    SelfSourceDesignatorSchema,
    OrganizationScopedPermissionSourceOptionsResponseSchema,
    type RevokePermissionRequest,
    RoleAssignmentGrantedPermissionPolicySummarySchema,
    RoleAssignmentDetailedSchema,
    RoleAssignmentPermissionsViewSchema,
    TerminalDeviceResourceDesignatorSchema,
    UserResourceDesignatorSchema,
    WildcardPermissionSourceDesignatorSchema,
} from '../permission.types';
import { PositiveIntSchema } from './common.schemas';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import {
    AuthorizationOpaqueCursorQuerySchema,
    AuthorizationPaginationLimitQuerySchema,
} from './authorization.schemas';
import { SearchTermsSchema } from './omnisearch.schemas';

// Re-export existing schemas
export {
    AllowedWebsiteTemplateResourceDesignatorSchema,
    CredentialResourceDesignatorSchema,
    IpgResourceDesignatorSchema,
    IpxCredentialDefinitionResourceDesignatorSchema,
    IpxWebResourceModelResourceDesignatorSchema,
    IpwResourceDesignatorSchema,
    LogicalTerminalResourceDesignatorSchema,
    MerchantAccountResourceDesignatorSchema,
    OrganizationIpxCredentialPolicyResourceDesignatorSchema,
    OrganizationResourceDesignatorSchema,
    PaymentMethodTokenResourceDesignatorSchema,
    PaymentRequestResourceDesignatorSchema,
    PaymentTransactionResourceDesignatorSchema,
    PermissionPolicyResourceDesignatorSchema,
    PermissionResourceDisplayMetadataSchema,
    PermissionGrantSchema,
    RawPermissionGrantSchema,
    StoredPermissionGrantArrayParser,
    StoredPermissionGrantParser,
    PermissionSourceDesignatorSchema,
    PermissionSourceDesignatorType,
    PermissionSourceRoleAssignmentListItemSchema,
    PolicyPermissionBundleListItemSchema,
    PolicyPermissionBundleRoleAssignmentListItemSchema,
    PermissionsBundleResourceDesignatorSchema,
    PermissionsBundlePublicSchema,
    PermissionsBundleSchema,
    ProcessorResourceDesignatorSchema,
    ResourceDesignatorSchema,
    RoleAssignmentResourceDesignatorSchema,
    RpaModuleResourceDesignatorSchema,
    SelfSourceDesignatorSchema,
    OrganizationScopedPermissionSourceOptionsResponseSchema,
    RoleAssignmentGrantedPermissionPolicySummarySchema,
    RoleAssignmentDetailedSchema,
    RoleAssignmentPermissionsViewSchema,
    TerminalDeviceResourceDesignatorSchema,
    UserResourceDesignatorSchema,
    WildcardPermissionSourceDesignatorSchema,
};

/**
 * Grant permission request
 */
export const GrantPermissionRequestSchema: z.ZodType<GrantPermissionRequest> = z.object({
    role_assignment_uuid: z.string().uuid(),
    permission: PermissionGrantSchema,
});

/**
 * Revoke permission request
 */
export const RevokePermissionRequestSchema: z.ZodType<RevokePermissionRequest> = z.object({
    role_assignment_uuid: z.string().uuid(),
    permission: PermissionGrantSchema,
});

export const RepairAuditedPermissionRequestSchema: z.ZodType<RepairAuditedPermissionRequest> =
    z.object({
        permissions_bundle_uuid: z.string().uuid(),
        permission_index: z.number().int().nonnegative(),
        permission: PermissionGrantSchema,
    });

/**
 * Assign bundle request
 */
export const AssignBundleRequestSchema: z.ZodType<AssignBundleRequest> = z.object({
    role_assignment_uuid: z.string().uuid(),
    bundle_uuid: z.string().uuid(),
});

/**
 * Create shared bundle request
 */
export const CreateSharedBundleRequestSchema = z.object({
    name: z.string().min(1),
    description: z.string().optional(),
    permissions: z.array(PermissionGrantSchema),
});

/**
 * Update bundle request
 */
export const UpdateBundleRequestSchema = z
    .object({
        name: z.string().min(1).optional(),
        description: z.string().optional(),
        permissions: z.array(PermissionGrantSchema).optional(),
        expected_permissions: z.array(PermissionGrantSchema).optional(),
        status: z.enum(Status).optional(),
    })
    .superRefine((request, context) => {
        if ((request.permissions === undefined) !== (request.expected_permissions === undefined)) {
            context.addIssue({
                code: 'custom',
                path: [request.permissions === undefined ? 'permissions' : 'expected_permissions'],
                message: 'permissions and expected_permissions must be supplied together',
            });
        }
    });

/**
 * List bundles response
 */
export const ListBundlesResponseSchema = z.object({
    bundles: z.array(PermissionsBundleSchema),
});

export const ListPublicBundlesResponseSchema: z.ZodType<ListPublicBundlesResponse> = z.object({
    bundles: z.array(PermissionsBundlePublicSchema),
});

export const SharedPermissionBundlesSearchRequestSchema: z.ZodType<SharedPermissionBundlesSearchRequest> =
    z
        .object({
            terms: SearchTermsSchema,
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: AuthorizationOpaqueCursorQuerySchema,
            include_descendants: z.boolean().optional(),
        })
        .strict();

export const SharedPermissionBundlesSearchPageResponseSchema: z.ZodType<SharedPermissionBundlesSearchPageResponse> =
    z.object({
        bundles: z.array(PermissionsBundlePublicSchema),
        pagination: OpaqueCursorPaginationSchema,
    });

/**
 * Role eligibility for actions
 */
export const RoleEligibilitySchema = z.object({
    role: z.string(),
    eligible_actions: z.array(z.enum(ALL_AUTHORIZABLE_ACTIONS)),
});

/**
 * Common param schemas
 */
export const BundleIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const BundleUuidParamSchema: z.ZodType<string> = z.string().uuid();

export const PolicyPermissionBundlesSearchRequestSchema: z.ZodType<PolicyPermissionBundlesSearchRequest> =
    z
        .object({
            terms: SearchTermsSchema,
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: AuthorizationOpaqueCursorQuerySchema,
            include_descendants: z.boolean().optional(),
            show_deleted: z.boolean().optional(),
        })
        .strict();

export const PolicyPermissionBundlesSearchPageResponseSchema: z.ZodType<PolicyPermissionBundlesSearchPageResponse> =
    z.object({
        bundles: z.array(PolicyPermissionBundleListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
    });

const PermissionSourceRoleAssignmentsSearchRequestSchemaBase = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationOpaqueCursorQuerySchema,
    })
    .strict();
export const PermissionSourceRoleAssignmentsSearchRequestSchema: z.ZodType<PermissionSourceRoleAssignmentsSearchRequest> =
    PermissionSourceRoleAssignmentsSearchRequestSchemaBase;

const PermissionSourceRoleAssignmentsSearchPageResponseSchemaBase = z.object({
    role_assignments: z.array(PermissionSourceRoleAssignmentListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});
export const PermissionSourceRoleAssignmentsSearchPageResponseSchema: z.ZodType<PermissionSourceRoleAssignmentsSearchPageResponse> =
    PermissionSourceRoleAssignmentsSearchPageResponseSchemaBase;

const PolicyPermissionBundleRoleAssignmentsSearchRequestSchemaBase = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationOpaqueCursorQuerySchema,
    })
    .strict();
export const PolicyPermissionBundleRoleAssignmentsSearchRequestSchema: z.ZodType<PolicyPermissionBundleRoleAssignmentsSearchRequest> =
    PolicyPermissionBundleRoleAssignmentsSearchRequestSchemaBase;

const PolicyPermissionBundleRoleAssignmentsSearchPageResponseSchemaBase = z.object({
    role_assignments: z.array(PolicyPermissionBundleRoleAssignmentListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});
export const PolicyPermissionBundleRoleAssignmentsSearchPageResponseSchema: z.ZodType<PolicyPermissionBundleRoleAssignmentsSearchPageResponse> =
    PolicyPermissionBundleRoleAssignmentsSearchPageResponseSchemaBase;

/**
 * Policy grants (role-assignment scoped).
 */
export const GrantPolicyPermissionBundleRequestSchema: z.ZodType<GrantPolicyPermissionBundleRequest> =
    z.object({
        permissions_bundle_uuid: z.string().uuid(),
    });

const ReorderPolicyPermissionGrantsRequestSchemaBase = z
    .object({
        permissions_bundle_uuids: z.array(z.string().uuid()).min(1),
    })
    .superRefine((value, ctx) => {
        const seen = new Set<string>();
        value.permissions_bundle_uuids.forEach((bundleUuid, index) => {
            if (seen.has(bundleUuid)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['permissions_bundle_uuids', index],
                    message: 'permissions_bundle_uuids must not contain duplicates',
                });
                return;
            }
            seen.add(bundleUuid);
        });
    });
export const ReorderPolicyPermissionGrantsRequestSchema: z.ZodType<ReorderPolicyPermissionGrantsRequest> =
    ReorderPolicyPermissionGrantsRequestSchemaBase;

export const ListPolicyPermissionGrantsResponseSchema: z.ZodType<ListPolicyPermissionGrantsResponse> =
    z.object({
        bundles: z.array(PermissionsBundlePublicSchema),
    });

export const RoleAssignmentPermissionAuditPathParamSchema: z.ZodType<string> = z.string().uuid();

export const OrganizationPermissionAuditPathParamSchema: z.ZodType<string> = z.string().uuid();
